import type { Street } from '@chipless/shared';
import { BOARD_CARD_TOTAL, boardCardCount, showsBoardCount } from '@chipless/shared';
import classes from './BoardIndicator.module.css';

/**
 * 卓上に出ているべきコミュニティカードの枚数（要件11.7 の補助）。
 *
 * ストリート名だけでは、いま何枚めくるべきかを毎回頭で変換することになる。5枚ぶんの枠を
 * 常に並べ、そのうち何枚が出ているべきかを塗り分けることで、枚数と残りを同時に示す。
 *
 * 添える文字は置かない。枠を数えれば分かることを文で言い直すと、ポット総額から視線を
 * 奪うだけになる。枚数はスクリーンリーダー向けに aria-label で伝える。
 *
 * カードの内容は表示しない。アプリはボードを知らず、知る必要もない。
 */
export function BoardIndicator({ street }: { street: Street | null }): React.ReactElement {
  const active = street !== null && showsBoardCount(street);
  const dealt = active ? boardCardCount(street) : 0;

  // ショーダウンに到達したのに卓上が揃っていない場面がある（全員オールインの自動消化）。
  // ここだけは注意を引く。
  const attention = street === 'SHOWDOWN';

  return (
    <div
      className={classes.row}
      role="img"
      aria-label={active ? `ボード ${dealt}枚` : 'ボードなし'}
      data-testid="board"
      data-board-cards={active ? dealt : ''}
    >
      {Array.from({ length: BOARD_CARD_TOTAL }, (_, i) => {
        const isDealt = i < dealt;
        const names = [classes.card];
        if (!active) names.push(classes.idle, classes.pending);
        else if (isDealt) names.push(classes.dealt);
        else names.push(classes.pending);
        if (active && isDealt && attention) names.push(classes.attention);
        return <div key={i} className={names.join(' ')} />;
      })}
    </div>
  );
}
